<template>
  <section class="stock-entry">
    <div class="stock-entry__brand">
      <img v-if="logoUrl" :src="logoUrl" alt="Pixelfoto Express" />
    </div>

    <div class="stock-entry__panel">
      <div class="stock-entry__eyebrow">Adobe Stock</div>
      <h1 class="stock-entry__title">Bild für den Editor vorbereiten</h1>
      <p class="stock-entry__text">
        Wir übernehmen zunächst die Vorschau aus Adobe Stock. Danach wählen Sie Kategorie, Produkt, Größe und Optionen frei im Editor.
      </p>

      <div class="stock-entry__preview">
        <div v-if="loading" class="stock-entry__placeholder">Vorschau wird geladen…</div>
        <img v-else-if="previewUrl" :src="previewUrl" :alt="previewTitle || 'Adobe Stock Vorschau'" />
        <div v-else class="stock-entry__placeholder">Keine Vorschau verfügbar</div>
      </div>

      <div class="stock-entry__meta">
        <div class="stock-entry__meta-title">{{ previewTitle || `Adobe Stock Asset ${assetId}` }}</div>
        <div class="stock-entry__meta-sub">Asset-ID: {{ assetId }}</div>
      </div>

      <div class="stock-entry__actions">
        <button class="stock-entry__btn stock-entry__btn--ghost" type="button" @click="goOverview">
          Zur Übersicht
        </button>
        <button class="stock-entry__btn stock-entry__btn--primary" type="button" @click="openEditor" :disabled="!assetId">
          Bild im Editor konfigurieren
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchAdobeStockImportPayload } from '~/services/stockImportService'
import { getWidgetConfig } from '~/runtime/widgetConfig'

const route = useRoute()
const router = useRouter()

const assetId = computed(() => {
  const raw = route.query.assetId ?? route.query.stockAssetId
  return String(Array.isArray(raw) ? raw[0] : raw || '').trim()
})

const loading = ref(false)
const previewUrl = ref('')
const previewTitle = ref('')
const logoUrl = computed(() => String((getWidgetConfig() as any)?.logoUrl || '').trim())
const overviewUrl = computed(() => String((getWidgetConfig() as any)?.overviewUrl || '/bikldinspiration').trim() || '/bikldinspiration')

watch(
  () => assetId.value,
  async (next) => {
    if (!next) {
      previewUrl.value = ''
      previewTitle.value = ''
      return
    }
    loading.value = true
    try {
      const payload = await fetchAdobeStockImportPayload(next)
      previewUrl.value = String(payload.previewUrl || '')
      previewTitle.value = String(payload.title || '')
    } catch {
      previewUrl.value = ''
      previewTitle.value = ''
    } finally {
      loading.value = false
    }
  },
  { immediate: true },
)

function openEditor() {
  if (!assetId.value) return
  const nextQuery = { ...route.query, stockContinue: '1' } as Record<string, any>
  const category = Number(Array.isArray(nextQuery.category) ? nextQuery.category[0] : nextQuery.category || nextQuery.categoryId || 0)
  const product = Number(Array.isArray(nextQuery.product) ? nextQuery.product[0] : nextQuery.product || nextQuery.productId || 0)
  router.push({
    path: category > 0 && product > 0 ? '/multi' : '/',
    query: nextQuery,
  }).catch(() => {})
}

function goOverview() {
  if (/^https?:\/\//i.test(overviewUrl.value)) {
    window.location.href = overviewUrl.value
    return
  }
  if (overviewUrl.value.startsWith('/')) {
    window.location.href = overviewUrl.value
    return
  }
  const nextQuery = { ...route.query } as Record<string, any>
  delete nextQuery.assetId
  delete nextQuery.stockAssetId
  delete nextQuery.stockContinue
  router.push({
    path: overviewUrl.value || '/',
    query: nextQuery,
  }).catch(() => {})
}
</script>

<style scoped>
.stock-entry{
  min-height: 100dvh;
  padding: clamp(20px, 4vw, 40px);
  background:
    radial-gradient(circle at top left, rgba(210, 184, 121, 0.18), transparent 34%),
    linear-gradient(180deg, #fbfaf7 0%, #f4f2ec 100%);
  display: grid;
  align-content: start;
  gap: 24px;
}
.stock-entry__brand{
  display:flex;
  justify-content:center;
}
.stock-entry__brand img{
  display:block;
  width:min(220px, 52vw);
  max-width:100%;
  height:auto;
}
.stock-entry__panel{
  width:min(760px, 100%);
  margin:0 auto;
  background:#fff;
  border:1px solid rgba(15,23,42,.08);
  border-radius:10px;
  box-shadow:0 22px 50px rgba(15,23,42,.08);
  padding:clamp(20px, 3.2vw, 32px);
  display:grid;
  gap:16px;
}
.stock-entry__eyebrow{
  font-size:12px;
  text-transform:uppercase;
  color:#a15e0a;
  font-weight:700;
}
.stock-entry__title{
  margin:0;
  font-size:clamp(26px, 4vw, 38px);
  line-height:1.04;
  color:#141414;
}
.stock-entry__text{
  margin:0;
  font-size:15px;
  line-height:1.55;
  color:rgba(15,23,42,.7);
  max-width:62ch;
}
.stock-entry__preview{
  aspect-ratio: 16 / 9;
  border:1px solid rgba(15,23,42,.08);
  border-radius:8px;
  background:#f7f5f1;
  overflow:hidden;
  display:flex;
  align-items:center;
  justify-content:center;
}
.stock-entry__preview img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
}
.stock-entry__placeholder{
  font-size:14px;
  color:rgba(15,23,42,.58);
}
.stock-entry__meta{
  display:grid;
  gap:4px;
}
.stock-entry__meta-title{
  font-size:18px;
  font-weight:700;
  color:#141414;
}
.stock-entry__meta-sub{
  font-size:13px;
  color:rgba(15,23,42,.56);
}
.stock-entry__actions{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
}
.stock-entry__btn{
  min-height:44px;
  padding:0 16px;
  border-radius:4px;
  font-size:14px;
  font-weight:700;
  border:2px solid rgba(15,23,42,.16);
  background:#fff;
  color:#141414;
  box-shadow:none;
}
.stock-entry__btn--primary{
  border-color:#d2b879;
  background:#d2b879;
  color:#fff;
}
@media (max-width: 640px){
  .stock-entry__actions{
    display:grid;
    grid-template-columns:1fr;
  }
}
</style>
